import React, { useRef, useState, useEffect } from 'react';
import HomeLanding from './HomeLanding';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform, AnimatePresence, useSpring } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ClientOnly } from './components/ClientOnly';
import { LocaleSync } from './components/LocaleSync';
import { useAppNavigation } from './hooks/useAppNavigation';
import { useLocaleRoute } from './hooks/useLocaleRoute';
import { pathForPage } from './routes';
import { getPageSeo } from './seo/getPageSeo';
import { applyDocumentSeo, resolveSiteUrl } from './seo/applyDocumentSeo';
import {
  Bot,
  BrainCircuit,
  Store,
  ArrowRight,
  ShieldCheck,
  Zap,
  X,
  Menu,
  Check,
  Sparkles,
  RefreshCw,
  Box,
  Layers,
  TrendingUp,
  Languages,
  ChevronDown,
  Play,
  Shield,
  MapPin,
  ShoppingBag,
  Camera,
  Settings,
  Network,
  Download
} from 'lucide-react';

// --- Assets & Constants ---
const COLORS = {
  bg: '#F0F9FF',      // Sky 50
  glass: 'rgba(255, 255, 255, 0.6)',
  primary: '#06B6D4', // Cyan-500
  accent: '#8B5CF6',  // Violet-500
  text: '#0F172A',    // Slate 900
};

// "Tactile 3D" Utility Classes
const GLASS_CARD = "bg-white/40 backdrop-blur-xl border border-white/60 shadow-[0_8px_32px_0_rgba(14,165,233,0.1)] rounded-3xl relative overflow-hidden group hover:border-cyan-400/50 hover:bg-white/60 transition-all duration-500";
const PLASTIC_BTN_PRIMARY = "bg-gradient-to-b from-cyan-400 to-cyan-600 text-white font-bold rounded-xl shadow-[0_4px_0_0_#0e7490,0_10px_20px_rgba(6,182,212,0.3)] active:shadow-none active:translate-y-1 transition-all border-t border-white/30";
const PLASTIC_BTN_SECONDARY = "bg-white text-cyan-600 font-bold rounded-xl shadow-[0_4px_0_0_#cbd5e1,0_10px_20px_rgba(14,165,233,0.05)] active:shadow-none active:translate-y-1 transition-all border border-sky-100 hover:bg-sky-50";
const NEON_INPUT = "w-full bg-white/60 border border-sky-200 rounded-xl p-4 text-slate-800 focus:outline-none focus:border-cyan-500 focus:shadow-[0_0_20px_rgba(6,182,212,0.2)] transition-all placeholder:text-slate-400 font-medium";

const SeoManager = ({ page }) => {
  const { i18n, t } = useTranslation();
  const { locale } = useLocaleRoute();

  useEffect(() => {
    const currentLanguage = locale || i18n.resolvedLanguage || i18n.language || 'en';
    const seo = getPageSeo({
      page,
      locale: currentLanguage,
      siteUrl: resolveSiteUrl(),
      t,
    });

    applyDocumentSeo(seo);
  }, [i18n.language, i18n.resolvedLanguage, locale, page, t]);

  return null;
};

// --- Shared Components ---

const LiquidMascot = ({ className, delay = 0, mood = "happy", type = "blob", colors = ["#22d3ee", "#06b6d4"] }) => {
    const paths = {
        blob: "M100 40 C145 40 175 70 175 115 C175 165 145 185 100 185 C55 185 25 165 25 115 C25 70 55 40 100 40 Z",
        robot: "M60 50 Q140 50 140 50 L140 130 Q140 170 100 170 Q60 170 60 130 Z M50 80 L40 70 M150 80 L160 70", 
        doll: "M100 30 C125 30 145 50 145 75 C145 95 130 110 115 115 L135 170 C135 180 65 180 65 170 L85 115 C70 110 55 95 55 75 C55 50 75 30 100 30 Z",
        cup: "M50 60 L60 160 C60 175 140 175 140 160 L150 60 C150 50 50 50 50 60 Z"
    };
    const eyePos = {
        blob: { lx: 75, rx: 125, y: 100 },
        robot: { lx: 80, rx: 120, y: 100 },
        doll: { lx: 85, rx: 115, y: 75 },
        cup: { lx: 80, rx: 120, y: 110 }
    };
    const currentEye = eyePos[type] || eyePos.blob;

    return (
      <motion.svg
        viewBox="0 0 200 200"
        className={className}
        initial={{ y: 0, scale: 0.8, opacity: 0 }}
        animate={{ y: [-5, 5, -5], rotate: [-2, 2, -2], scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        transition={{ 
            y: { duration: 4, repeat: Infinity, ease: "easeInOut" },
            rotate: { duration: 5, repeat: Infinity, ease: "easeInOut" },
            scale: { duration: 0.5 },
            opacity: { duration: 0.5 }
        }}
      >
        <defs>
          <linearGradient id={`grad-${type}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={colors[0]} />
            <stop offset="100%" stopColor={colors[1]} />
          </linearGradient>
          <filter id={`glow-${type}`}>
            <feGaussianBlur stdDeviation="2.5" result="coloredBlur" />
            <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>
        <motion.path 
          d={paths[type] || paths.blob} fill={`url(#grad-${type})`} filter={`url(#glow-${type})`}
          animate={{ d: type === 'blob' ? [paths.blob, "M100 35 C150 35 180 75 180 115 C180 160 140 190 100 190 C60 190 20 160 20 115 C20 75 50 35 100 35 Z", paths.blob] : undefined }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />
        <path d={`M${currentEye.lx} ${currentEye.y - 45} Q100 ${currentEye.y - 55} ${currentEye.rx} ${currentEye.y - 45}`} stroke="white" strokeWidth="4" strokeLinecap="round" fill="none" opacity="0.3" />
        <g>
            <motion.ellipse cx={currentEye.lx} cy={currentEye.y} rx="10" ry="15" fill="#0B1120" animate={{ ry: [15, 2, 15] }} transition={{ duration: 3, repeat: Infinity, times: [0, 0.95, 1], delay: delay }} />
            <circle cx={currentEye.lx + 3} cy={currentEye.y - 4} r="3" fill="white" />
            <motion.ellipse cx={currentEye.rx} cy={currentEye.y} rx="10" ry="15" fill="#0B1120" animate={{ ry: [15, 2, 15] }} transition={{ duration: 3, repeat: Infinity, times: [0, 0.95, 1], delay: delay }} />
            <circle cx={currentEye.rx + 3} cy={currentEye.y - 4} r="3" fill="white" />
        </g>
        {mood === 'happy' && (<path d={`M${currentEye.lx + 10} ${currentEye.y + 30} Q100 ${currentEye.y + 40} ${currentEye.rx - 10} ${currentEye.y + 30}`} stroke="#0B1120" strokeWidth="4" strokeLinecap="round" fill="none" />)}
      </motion.svg>
    );
};

const ScrollWaterDropInner = () => {
  const { scrollYProgress } = useScroll();
  const smoothProgress = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });
  const y = useTransform(smoothProgress, [0, 1], ['10vh', '90vh']);
  const opacity = useTransform(smoothProgress, [0, 0.05, 0.95, 1], [0, 1, 1, 0]);
  return (
    <motion.div style={{ opacity }} className="fixed right-6 md:right-10 top-0 bottom-0 w-12 pointer-events-none z-50 hidden md:flex flex-col items-center justify-center">
      <div className="absolute inset-y-20 w-[2px] bg-gradient-to-b from-transparent via-cyan-400/30 to-transparent"></div>
      <motion.div style={{ top: y }} className="absolute w-6 h-6 bg-cyan-400 z-10 rounded-[0%_50%_50%_50%] rotate-45 shadow-[0_0_20px_rgba(6,182,212,0.6),inset_-2px_-2px_4px_rgba(255,255,255,0.5)]">
        <div className="absolute top-[25%] left-[25%] w-2 h-2 bg-white rounded-full blur-[0.5px]"></div>
        <div className="absolute bottom-[10%] right-[10%] w-3 h-3 bg-cyan-300 rounded-full blur-[2px] opacity-60"></div>
      </motion.div>
      <motion.div style={{ top: y }} className="absolute w-12 h-16 bg-cyan-400/20 rounded-full blur-xl -translate-y-1/4" />
    </motion.div>
  );
};

const ScrollWaterDrop = () => (
  <ClientOnly>
    <ScrollWaterDropInner />
  </ClientOnly>
);

const PricingCard = ({ tier, price, audience, features, recommended = false }) => (
  <motion.div whileHover={{ y: -10, rotateX: 5 }} style={{ transformStyle: 'preserve-3d', perspective: '1000px' }} className={`p-8 flex flex-col justify-between h-full ${GLASS_CARD} ${recommended ? 'border-cyan-400 shadow-[0_0_40px_rgba(6,182,212,0.15)] bg-white/50' : ''}`}>
    {recommended && <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyan-400 to-violet-500"></div>}
    <div className="relative z-10">
      <h3 className="text-xl font-bold text-cyan-600 uppercase tracking-widest mb-2 font-mono">{tier}</h3>
      <div className="text-5xl font-black text-slate-800 mb-4 tracking-tight">{price}</div>
      <p className="text-sm font-medium pb-6 mb-6 text-slate-500 border-b border-sky-100">{audience}</p>
      <ul className="space-y-4">{Array.isArray(features) ? features.map((f, i) => (<li key={i} className="flex items-center gap-3 text-sm text-slate-600 font-medium"><div className="w-5 h-5 rounded bg-cyan-50 flex items-center justify-center flex-shrink-0 text-cyan-600 border border-cyan-100"><Check size={12} strokeWidth={3} /></div>{f}</li>)) : null}</ul>
    </div>
  </motion.div>
);

const FeatureCard = ({ icon: Icon, title, desc, delay }) => (
  <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: delay }} className={`${GLASS_CARD} p-8 h-full flex flex-col items-start`}>
    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-white to-sky-50 shadow-[inset_0_2px_4px_rgba(255,255,255,0.8),0_4px_10px_rgba(14,165,233,0.1)] border border-white flex items-center justify-center mb-6 text-cyan-500 group-hover:text-cyan-600 group-hover:scale-110 transition-all duration-300 relative"><Icon size={32} strokeWidth={1.5} className="relative z-10" /></div>
    <h3 className="text-xl font-bold text-slate-800 mb-3 group-hover:text-cyan-600 transition-colors">{title}</h3>
    <p className="text-slate-500 leading-relaxed flex-grow font-medium">{desc}</p>
  </motion.div>
);

const Marquee = () => (
    <div className="border-y border-white/40 bg-white/20 backdrop-blur-md py-4 overflow-hidden whitespace-nowrap select-none relative z-20">
      <div className="absolute inset-0 bg-cyan-500/5"></div>
      <motion.div animate={{ x: [0, -1000] }} transition={{ repeat: Infinity, duration: 30, ease: "linear" }} className="text-cyan-900/20 text-4xl font-black uppercase flex gap-16 items-center tracking-tighter">
        {[...Array(2)].map((_, i) => (<React.Fragment key={i}><span className="text-cyan-500/30">Physical_AI</span><span>//</span><span>Liquid_Neural_Net</span><span>//</span><span>Holographic_Presence</span><span>//</span><span>Retail_Deployable</span><span>//</span><span>Gemini_Core_v2.5</span><span>//</span></React.Fragment>))}
      </motion.div>
    </div>
);

const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { locale, page } = useLocaleRoute();
  const [isOpen, setIsOpen] = useState(false);

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'zh-CN', name: '简体中文', flag: '🇨🇳' },
    { code: 'zh-TW', name: '繁體中文', flag: '🇭🇰' },
    { code: 'ko', name: '한국어', flag: '🇰🇷' },
    { code: 'ja', name: '日本語', flag: '🇯🇵' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  ];

  const currentLang = languages.find((lang) => lang.code === locale) || languages[0];

  /**
   * @param {string} langCode
   */
  const changeLanguage = (langCode) => {
    const target = pathForPage(page, langCode);
    navigate(`${target}${location.hash}`);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-bold text-slate-600 hover:text-cyan-600 hover:bg-sky-50 transition-all"
      >
        <Languages size={16} />
        <span className="hidden sm:inline">{currentLang.flag} {currentLang.name}</span>
        <span className="sm:hidden">{currentLang.flag}</span>
        <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-sky-100 overflow-hidden z-50"
          >
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                className={`w-full px-4 py-3 text-left text-sm font-medium transition-all flex items-center gap-3 ${
                  locale === lang.code
                    ? 'bg-cyan-50 text-cyan-700'
                    : 'text-slate-600 hover:bg-sky-50 hover:text-cyan-600'
                }`}
              >
                <span className="text-lg">{lang.flag}</span>
                <span>{lang.name}</span>
                {locale === lang.code && <Check size={14} className="ml-auto text-cyan-600" />}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const MascotGenerator = () => {
  const { t } = useTranslation();
  const [brand, setBrand] = useState('');
  const [industry, setIndustry] = useState('');
  const [loading, setLoading] = useState(false);
  const [mascot, setMascot] = useState(null);
  const [showUnavailableMessage, setShowUnavailableMessage] = useState(false);
  const [activeVariantIndex, setActiveVariantIndex] = useState(0);
  const unavailableMessageTimeoutRef = useRef(null);

  const mascotVariants = [
      { type: 'blob', colors: ['#22d3ee', '#06b6d4'] },
      { type: 'robot', colors: ['#a78bfa', '#8b5cf6'] },
      { type: 'doll', colors: ['#f472b6', '#db2777'] },
      { type: 'cup', colors: ['#34d399', '#10b981'] }
  ];

  useEffect(() => {
    if (loading || mascot) return;
    const interval = setInterval(() => {
        setActiveVariantIndex((prev) => (prev + 1) % mascotVariants.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [loading, mascot]);

  useEffect(() => {
    return () => {
      if (unavailableMessageTimeoutRef.current) {
        clearTimeout(unavailableMessageTimeoutRef.current);
      }
    };
  }, []);

  const generateMascot = () => {
    if (!brand || !industry) return;
    setShowUnavailableMessage(true);

    if (unavailableMessageTimeoutRef.current) {
      clearTimeout(unavailableMessageTimeoutRef.current);
    }

    unavailableMessageTimeoutRef.current = window.setTimeout(() => {
      setShowUnavailableMessage(false);
      unavailableMessageTimeoutRef.current = null;
    }, 3000);

    // Real mascot generation is intentionally disabled for now.
    // To re-enable it later, move the API key to the backend or a protected env-based API route,
    // then replace the placeholder message above with the original Gemini request flow.
    //
    // Example of the previous direct-call approach kept here for reference only:
    //
    // const apiKey = '';
    // setLoading(true);
    // setShowUnavailableMessage(false);
    // const response = await fetch(
    //   `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-09-2025:generateContent?key=${apiKey}`,
    //   {
    //     method: 'POST',
    //     headers: { 'Content-Type': 'application/json' },
    //     body: JSON.stringify({
    //       contents: [{
    //         parts: [{
    //           text: `You are an expert creative director for a branding agency. Create a unique, memorable mascot persona for a brand named "${brand}" in the "${industry}" industry. Return response in JSON: {name, role, catchphrase, backstory, traits: [], visual}`
    //         }]
    //       }],
    //       generationConfig: { responseMimeType: 'application/json' }
    //     })
    //   }
    // );
    // const data = await response.json();
    // if (data.error) throw new Error(data.error.message);
    // const result = JSON.parse(data.candidates[0].content.parts[0].text);
    // setMascot(result);
  };

  const currentVariant = mascotVariants[activeVariantIndex];

  return (
    <div className={`h-full min-h-[550px] flex flex-col relative overflow-hidden rounded-3xl bg-white/40 border border-white/60 backdrop-blur-md shadow-[0_20px_50px_-12px_rgba(0,0,0,0.1)]`}>
       <div className="absolute inset-0 bg-[linear-gradient(rgba(6,182,212,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(6,182,212,0.05)_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none"></div>
       <div className="flex justify-between items-center p-4 border-b border-white/50 bg-white/30">
            <div className="flex gap-2 items-center">
                <div className="w-2 h-2 rounded-full bg-cyan-500 animate-pulse shadow-[0_0_10px_#06b6d4]"></div>
                <span className="text-xs font-mono text-cyan-700 uppercase tracking-widest font-bold">{t('mascotGenerator.status')}</span>
            </div>
            <div className="text-xs font-mono text-slate-400">{t('mascotGenerator.systemVersion')}</div>
       </div>

       {!mascot ? (
         <div className="p-8 flex flex-col h-full justify-center relative z-10">
            <div className="mb-8 text-center relative flex flex-col items-center">
              <div className="absolute inset-0 bg-cyan-400/20 blur-3xl rounded-full opacity-30 animate-pulse"></div>
              <div className="relative w-40 h-40 mb-4 flex items-center justify-center">
                  <AnimatePresence mode="wait">
                    {loading ? (
                        <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 flex items-center justify-center">
                            <LiquidMascot className="w-full h-full opacity-50 blur-[2px]" mood="thinking" />
                            <RefreshCw size={32} className="absolute text-cyan-600 animate-spin" />
                        </motion.div>
                    ) : (
                        <motion.div key={currentVariant.type} className="w-full h-full">
                            <LiquidMascot className="w-full h-full drop-shadow-[0_10px_20px_rgba(6,182,212,0.2)]" type={currentVariant.type} colors={currentVariant.colors} />
                        </motion.div>
                    )}
                  </AnimatePresence>
              </div>
              <h3 className="font-black text-3xl text-slate-800 mb-2 tracking-tight relative z-10">{t('mascotGenerator.title')}</h3>
              <p className="text-slate-500 font-medium font-mono text-sm relative z-10">{t('mascotGenerator.subtitle')}</p>
            </div>
            <div className="space-y-6">
              <div>
                <label className="block text-[10px] font-bold text-cyan-600 uppercase tracking-widest mb-2 ml-1">{t('mascotGenerator.brandLabel')}</label>
                <input type="text" value={brand} onChange={(e) => setBrand(e.target.value)} placeholder={t('mascotGenerator.brandPlaceholder')} className={NEON_INPUT} />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-cyan-600 uppercase tracking-widest mb-2 ml-1">{t('mascotGenerator.industryLabel')}</label>
                <input type="text" value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder={t('mascotGenerator.industryPlaceholder')} className={NEON_INPUT} />
              </div>
              {showUnavailableMessage && <p className="text-red-500 text-xs text-center font-mono border border-red-200 bg-red-50 p-2 rounded">{t('mascotGenerator.unavailableMessage')}</p>}
              <button onClick={generateMascot} disabled={loading || !brand || !industry} className={`w-full py-5 text-sm uppercase tracking-widest ${loading ? 'opacity-50 cursor-not-allowed' : ''} ${PLASTIC_BTN_PRIMARY} relative overflow-hidden group`}>
                <span className="relative z-10 flex items-center justify-center gap-2">{loading ? t('mascotGenerator.ctaLoading') : <>{t('mascotGenerator.cta')} <Sparkles size={16}/></>}</span>
              </button>
              <button
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="w-full py-3 text-sm text-cyan-600 font-bold hover:text-cyan-700 transition-colors mt-2"
              >
                {t('mascotGenerator.orContact')} →
              </button>
            </div>
         </div>
       ) : (
         <div className="h-full flex flex-col relative animate-in fade-in zoom-in-95 duration-500">
           <div className="relative flex-grow bg-gradient-to-b from-sky-50 to-transparent p-6 overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-cyan-400 shadow-[0_0_20px_#22d3ee]"></div>
              <div className="flex justify-between items-start mb-6">
                <div>
                  <div className="text-[10px] font-mono text-cyan-600 mb-1 border border-cyan-200 px-2 py-0.5 rounded inline-block bg-white/50">{t('mascotGenerator.randomId')} {Math.floor(Math.random() * 9999)}</div>
                  <h3 className="font-black text-4xl text-slate-800 tracking-tighter">{mascot.name}</h3>
                  <p className="text-violet-500 font-mono text-sm font-bold">{mascot.role}</p>
                </div>
                <button onClick={() => setMascot(null)} className="text-slate-400 hover:text-cyan-600 transition-colors"><RefreshCw size={20} /></button>
              </div>
              <div className="space-y-4 relative z-10">
                 <div className="bg-white/60 border border-white p-4 rounded-xl shadow-sm"><p className="text-cyan-600 font-bold italic text-lg text-center">"{mascot.catchphrase}"</p></div>
                 <div className="grid grid-cols-2 gap-2">{mascot.traits.map((trait, i) => (<div key={i} className="bg-white/40 border border-sky-100 px-3 py-2 rounded-lg text-xs font-mono text-center text-slate-600 uppercase font-bold">{trait}</div>))}</div>
                 <div className="space-y-2"><p className="text-[10px] text-slate-400 uppercase font-bold tracking-widest">Visual Analysis</p><p className="text-sm text-slate-600 leading-relaxed border-l-2 border-cyan-200 pl-3 font-medium">{mascot.visual}</p></div>
              </div>
           </div>
           <div className="p-4 bg-white/40 border-t border-white/50 backdrop-blur-md"><button className={`w-full py-4 text-xs uppercase tracking-widest ${PLASTIC_BTN_PRIMARY}`}>{t('mascotGenerator.export')}</button></div>
         </div>
       )}
    </div>
  );
};

// --- Storytelling Scenes for the "Why" Page ---

const WhyScene1 = () => (
    <div className="relative w-full h-64 md:h-80 flex items-center justify-center">
        {[...Array(5)].map((_, i) => (
            <motion.div 
                key={i}
                className="absolute w-20 h-20 bg-slate-200 rounded-xl border-4 border-slate-300 flex items-center justify-center"
                animate={{ opacity: [0, 1], scale: [0.8, 1] }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                style={{ left: `${10 + i * 20}%`, top: '50%', translateY: '-50%', zIndex: 0 }}
            >
                <Box className="text-slate-400" size={32} />
            </motion.div>
        ))}
        <motion.div 
            className="absolute z-10"
            animate={{ scale: [0, 1.5], y: [50, -20] }}
            transition={{ type: "spring", stiffness: 100, delay: 0.6 }}
        >
            <div className="relative">
                <div className="absolute inset-0 bg-white/50 blur-xl rounded-full"></div>
                <LiquidMascot className="w-32 h-32 drop-shadow-2xl" type="doll" colors={["#f472b6", "#db2777"]} />
                <motion.div 
                    className="absolute -top-4 -right-4 bg-yellow-400 text-yellow-900 text-xs font-black px-2 py-1 rounded-full border-2 border-white shadow-lg"
                    animate={{ scale: [0, 1] }}
                    transition={{ delay: 1 }}
                >
                    POP!
                </motion.div>
            </div>
        </motion.div>
    </div>
);

const WhyScene2 = () => (
    <div className="relative w-full h-64 md:h-80 flex flex-col items-center justify-center">
        <div className="relative w-40 h-full flex flex-col items-center">
            {/* Input Stream */}
            <div className="absolute top-0 w-full flex justify-center gap-2">
                {[...Array(3)].map((_, i) => (
                    <motion.div 
                        key={i}
                        className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center text-[8px] font-mono text-slate-400 border border-slate-200"
                        animate={{ y: [0, 100], opacity: [1, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.5, ease: "linear" }}
                    >
                        DATA
                    </motion.div>
                ))}
            </div>
            {/* The Machine */}
            <div className="relative z-10 mt-16 bg-white/80 backdrop-blur-md border-2 border-cyan-200 rounded-2xl p-4 shadow-xl w-48 h-32 flex items-center justify-center">
                <Sparkles className="text-cyan-400 animate-pulse absolute top-2 right-2" size={16} />
                <div className="text-center">
                    <div className="text-xs font-black text-cyan-600 mb-1">GENERATING...</div>
                    <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <motion.div className="h-full bg-cyan-400" animate={{ width: ["0%", "100%"] }} transition={{ duration: 1, repeat: Infinity }} />
                    </div>
                </div>
            </div>
            {/* Output */}
            <motion.div 
                className="absolute bottom-0 z-0"
                whileInView={{ scale: [0, 1], opacity: [0, 1], y: [20, 0] }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: 0.5 }}
            >
                <LiquidMascot className="w-24 h-24" type="robot" colors={["#a78bfa", "#8b5cf6"]} />
            </motion.div>
        </div>
    </div>
);

const WhyScene3 = () => (
    <div className="relative w-full h-64 md:h-80 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(14,165,233,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(14,165,233,0.1)_1px,transparent_1px)] bg-[size:20px_20px] [perspective:500px] [transform:rotateX(45deg)] opacity-50"></div>
        <div className="absolute inset-4 border-2 border-dashed border-cyan-200 rounded-3xl pointer-events-none"></div>
        <div className="grid grid-cols-4 gap-4 relative z-10">
            {[...Array(4)].map((_, i) => (
                <motion.div 
                    key={i}
                    whileInView={{ scale: [0, 1] }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ delay: i * 0.1 }}
                    className="relative"
                >
                    <LiquidMascot className="w-16 h-16 opacity-80" type="blob" colors={["#34d399", "#10b981"]} />
                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-8 h-1 bg-green-400 rounded-full shadow-[0_0_5px_#4ade80]"></div>
                </motion.div>
            ))}
        </div>
        <motion.div 
            className="absolute z-20 pointer-events-none"
            animate={{ x: [50, -50, 50], y: [20, -20, 20] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        >
            <div className="w-8 h-8 bg-white rounded-full border-4 border-slate-900 shadow-xl flex items-center justify-center"><div className="w-2 h-2 bg-cyan-500 rounded-full"></div></div>
        </motion.div>
        <div className="absolute bottom-8 right-8 bg-green-100 text-green-700 px-3 py-1 rounded-lg font-bold text-xs border border-green-200 shadow-sm flex items-center gap-1">
            <TrendingUp size={12} /> COST: LOW
        </div>
    </div>
);

const WhyPage = () => {
    const { localizedPath } = useLocaleRoute();
    const { t } = useTranslation();
    return (
        <div className="pt-32 pb-20 px-6 container mx-auto relative z-10">
            <motion.div
                className="text-center mb-20"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
            >
                <div className="inline-flex items-center gap-2 bg-white/60 border border-sky-100 px-4 py-1.5 rounded-full font-mono text-cyan-600 text-xs font-bold mb-6 shadow-sm">
                    <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500"></span></span>
                    {t('why.badge')}
                </div>
                <h1 className="text-5xl md:text-7xl font-black text-slate-900 mb-6 tracking-tight">{t('why.title')} <span className="text-cyan-500">{t('why.titleHighlight')}</span></h1>
                <p className="text-slate-500 text-xl max-w-2xl mx-auto font-medium">{t('why.subtitle')}</p>
            </motion.div>

            <div className="space-y-32">
                {/* Scene 1: Stand Out */}
                <div className="flex flex-col md:flex-row items-center gap-16">
                    <motion.div 
                        className="w-full md:w-1/2"
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, amount: 0.4 }}
                        transition={{ duration: 0.8 }}
                    >
                        <motion.div 
                            className={`${GLASS_CARD} p-8 md:p-12`}
                            animate={{ y: [-10, 10] }}
                            transition={{ repeat: Infinity, repeatType: "reverse", duration: 4 }}
                        >
                            <WhyScene1 />
                        </motion.div>
                    </motion.div>
                    {/* NO ENTRY ANIMATION ON TEXT */}
                    <div className="w-full md:w-1/2 space-y-6">
                        <div className="w-16 h-16 bg-pink-100 rounded-2xl flex items-center justify-center text-pink-600 mb-4 border border-pink-200 shadow-sm"><Sparkles size={32} /></div>
                        <h2 className="text-4xl font-black text-slate-900 leading-tight">{t('why.standOut.title')}</h2>
                        <p className="text-slate-600 text-lg leading-relaxed font-medium">{t('why.standOut.description')}</p>
                    </div>
                </div>

                {/* Scene 2: Speed */}
                <div className="flex flex-col-reverse md:flex-row items-center gap-16">
                    {/* NO ENTRY ANIMATION ON TEXT */}
                    <div className="w-full md:w-1/2 space-y-6 md:text-right flex flex-col md:items-end">
                        <div className="w-16 h-16 bg-violet-100 rounded-2xl flex items-center justify-center text-violet-600 mb-4 border border-violet-200 shadow-sm"><Zap size={32} /></div>
                        <h2 className="text-4xl font-black text-slate-900 leading-tight">{t('why.speed.title')}</h2>
                        <p className="text-slate-600 text-lg leading-relaxed font-medium">{t('why.speed.description')}</p>
                    </div>
                    <motion.div 
                        className="w-full md:w-1/2"
                        initial={{ opacity: 0, x: 50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, amount: 0.4 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        <motion.div 
                            className={`${GLASS_CARD} p-8 md:p-12`}
                            animate={{ y: [10, -10] }}
                            transition={{ repeat: Infinity, repeatType: "reverse", duration: 5 }}
                        >
                            <WhyScene2 />
                        </motion.div>
                    </motion.div>
                </div>

                {/* Scene 3: Control */}
                <div className="flex flex-col md:flex-row items-center gap-16">
                    <motion.div 
                        className="w-full md:w-1/2"
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, amount: 0.4 }}
                        transition={{ duration: 0.8 }}
                    >
                        <motion.div 
                            className={`${GLASS_CARD} p-8 md:p-12`}
                            animate={{ y: [-10, 10] }}
                            transition={{ repeat: Infinity, repeatType: "reverse", duration: 4.5 }}
                        >
                            <WhyScene3 />
                        </motion.div>
                    </motion.div>
                    {/* NO ENTRY ANIMATION ON TEXT */}
                    <div className="w-full md:w-1/2 space-y-6">
                        <div className="w-16 h-16 bg-cyan-100 rounded-2xl flex items-center justify-center text-cyan-600 mb-4 border border-cyan-200 shadow-sm"><TrendingUp size={32} /></div>
                        <h2 className="text-4xl font-black text-slate-900 leading-tight">{t('why.cost.title')}</h2>
                        <p className="text-slate-600 text-lg leading-relaxed font-medium">{t('why.cost.description')}</p>
                    </div>
                </div>
            </div>

            <div className="mt-32 text-center">
                <Link to={localizedPath('home')} className={`inline-block px-10 py-5 text-lg uppercase tracking-widest ${PLASTIC_BTN_PRIMARY}`}>{t('hero.cta')}</Link>
            </div>
        </div>
    );
};

// --- Contact Form Component ---

const ContactForm = ({ copy } = {}) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        company: '',
        message: copy?.initialMessage || ''
    });
    const [status, setStatus] = useState('idle'); // idle, loading, success, error
    const [error, setError] = useState('');

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('loading');
        setError('');

        try {
            const submission = copy?.context
                ? { ...formData, message: `${copy.context}\n\n${formData.message}` }
                : formData;
            const response = await fetch('/api/enquiry', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(submission)
            });

            const data = await response.json();

            if (response.ok) {
                setStatus('success');
                setFormData({ name: '', email: '', company: '', message: '' });
                // Reset success message after 5 seconds
                setTimeout(() => setStatus('idle'), 5000);
            } else {
                setStatus('error');
                setError(data.error || 'Failed to submit enquiry. Please try again.');
            }
        } catch (err) {
            setStatus('error');
            setError('Network error. Please check your connection and try again.');
        }
    };

    if (status === 'success') {
        return (
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-12"
            >
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Check className="text-green-500" size={40} strokeWidth={3} />
                </div>
                <h3 className="text-3xl font-black text-slate-800 mb-4">{t('contact.form.success')}</h3>
                <p className="text-slate-600 text-lg">{t('contact.form.successMessage')}</p>
            </motion.div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                    <label htmlFor={copy ? 'preview-name' : undefined} className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-2">{t('contact.form.name')}</label>
                    <input
                        type="text"
                        id={copy ? 'preview-name' : undefined}
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder={t('contact.form.namePlaceholder')}
                        className={NEON_INPUT}
                        required
                    />
                </div>
                <div className="space-y-2">
                    <label htmlFor={copy ? 'preview-email' : undefined} className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-2">{t('contact.form.email')}</label>
                    <input
                        type="email"
                        id={copy ? 'preview-email' : undefined}
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder={t('contact.form.emailPlaceholder')}
                        className={NEON_INPUT}
                        required
                    />
                </div>
            </div>
            <div className="space-y-2">
                <label htmlFor={copy ? 'preview-company' : undefined} className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-2">{t('contact.form.company')}</label>
                <input
                    type="text"
                    id={copy ? 'preview-company' : undefined}
                        name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder={t('contact.form.companyPlaceholder')}
                    className={NEON_INPUT}
                />
            </div>
            <div className="space-y-2">
                <label htmlFor={copy ? 'preview-message' : undefined} className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-2">{t('contact.form.message')}</label>
                <textarea
                    id={copy ? 'preview-message' : undefined}
                        name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="4"
                    placeholder={copy?.messagePlaceholder || t('contact.form.messagePlaceholder')}
                    className={`${NEON_INPUT} resize-none`}
                    required
                ></textarea>
            </div>
            {error && (
                <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl border border-red-100 text-sm font-medium">
                    {error}
                </div>
            )}
            <button
                type="submit"
                disabled={status === 'loading'}
                className={`w-full py-5 text-lg uppercase tracking-widest ${PLASTIC_BTN_PRIMARY} mt-4 ${status === 'loading' ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
                {status === 'loading' ? (
                    <span className="flex items-center justify-center gap-2">
                        <RefreshCw size={20} className="animate-spin" />
                        {t('contact.form.submitting')}
                    </span>
                ) : (copy?.submit || t('contact.form.submit'))}
            </button>
        </form>
    );
};

const PromoVideoSection = () => {
    const { t, i18n } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const currentLanguage = i18n.resolvedLanguage || i18n.language;
    const isChineseVideo = currentLanguage === 'zh-CN' || currentLanguage === 'zh-TW';
    const videoSrc = isChineseVideo ? '/videos/aquamind-chi-web.mp4' : '/videos/aquamind-eng-web.mp4';
    const posterSrc = isChineseVideo ? '/videos/aquamind-chi-poster.jpg' : '/videos/aquamind-eng-poster.jpg';

    useEffect(() => {
        setIsOpen(false);
    }, [videoSrc, posterSrc]);

    return (
        <section id="product-video" className="relative z-10 py-24 px-6">
            <div className="max-w-6xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="mb-10 text-center"
                >
                    <div className="inline-flex items-center gap-2 bg-white/70 border border-sky-100 px-4 py-1.5 rounded-full font-mono text-cyan-600 text-xs font-bold mb-6 shadow-sm">
                        {t('promoVideo.badge')}
                    </div>
                    <h2 className="text-3xl md:text-5xl font-black text-slate-900 mb-4 tracking-tight">
                        {t('promoVideo.title')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-500 to-violet-500">{t('promoVideo.titleHighlight')}</span>
                    </h2>
                    <p className="text-slate-500 text-lg max-w-3xl mx-auto font-medium">
                        {t('promoVideo.subtitle')}
                    </p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                    className={`${GLASS_CARD} p-4 md:p-6`}
                >
                    <div className="relative overflow-hidden rounded-[2rem] border border-white/70 bg-slate-950 shadow-[0_30px_80px_rgba(15,23,42,0.25)]">
                        {!isOpen ? (
                            <button
                                type="button"
                                onClick={() => setIsOpen(true)}
                                className="group relative block w-full text-left"
                                aria-label={t('promoVideo.play')}
                            >
                                <div className="aspect-video w-full bg-slate-950">
                                    <img
                                        src={posterSrc}
                                        alt={t('promoVideo.posterAlt')}
                                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                                        loading="lazy"
                                    />
                                </div>
                                <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.06)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.06)_1px,transparent_1px)] bg-[size:32px_32px] opacity-30" />
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/35 to-slate-950/10" />
                                <div className="absolute left-6 top-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.24em] text-white/90 backdrop-blur-md">
                                    <span className="h-2 w-2 rounded-full bg-cyan-400 shadow-[0_0_12px_rgba(34,211,238,0.8)]" />
                                    {t('promoVideo.runtime')}
                                </div>
                                <div className="absolute inset-x-0 bottom-0 p-6 md:p-10">
                                    <div className="max-w-2xl">
                                        <p className="mb-3 text-xs font-bold uppercase tracking-[0.28em] text-cyan-200">{t('promoVideo.kicker')}</p>
                                        <h3 className="mb-3 text-2xl font-black text-white md:text-4xl">{t('promoVideo.cardTitle')}</h3>
                                        <p className="max-w-xl text-sm font-medium leading-relaxed text-slate-200 md:text-base">
                                            {t('promoVideo.cardBody')}
                                        </p>
                                    </div>
                                </div>
                                <div className="absolute right-6 top-1/2 flex h-20 w-20 -translate-y-1/2 items-center justify-center rounded-full border border-white/40 bg-white/15 text-white shadow-[0_0_30px_rgba(34,211,238,0.25)] backdrop-blur-md transition-transform duration-300 group-hover:scale-105">
                                    <Play size={28} fill="currentColor" className="ml-1" />
                                </div>
                            </button>
                        ) : (
                            <div className="aspect-video w-full bg-black">
                                <video
                                    className="h-full w-full"
                                    controls
                                    autoPlay
                                    playsInline
                                    preload="metadata"
                                    src={videoSrc}
                                />
                            </div>
                        )}
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

const BROCHURES = [
  { id: 'retailRobots', file: 'retail-robots', sizes: { en: '1.0 MB', cn: '979 KB' } },
  { id: 'ipOperations', file: 'ip-operations', sizes: { en: '1.2 MB', cn: '1.2 MB' } },
];

const BrochureSection = ({ retailOnly = false }) => {
  const { t, i18n } = useTranslation();
  const prefersChinese = (i18n.resolvedLanguage || i18n.language || 'en').startsWith('zh');
  const languages = prefersChinese ? ['cn', 'en'] : ['en', 'cn'];
  const brochures = retailOnly ? BROCHURES.filter((brochure) => brochure.id === 'retailRobots') : BROCHURES;
  const getPath = (brochure, language, extension) => `/brochures/aquamind-${brochure.file}-${language}.${extension}`;

  return (
    <section id="brochures" className="relative z-10 px-6 py-20">
      <div className="max-w-6xl mx-auto">
        <div className="mb-10 text-center">
          <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">{t('brochures.title')}</h2>
          <p className="text-slate-500 font-medium max-w-2xl mx-auto">{t('brochures.subtitle')}</p>
        </div>
        <div className={`grid gap-6 ${retailOnly ? 'max-w-3xl mx-auto' : 'lg:grid-cols-2'}`}>
          {brochures.map((brochure) => (
            <article key={brochure.id} className={`${GLASS_CARD} p-6 flex flex-col sm:flex-row gap-6`}>
              <a
                href={getPath(brochure, languages[0], 'pdf')}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${t('brochures.view')} — ${t(`brochures.${brochure.id}.title`)} (${languages[0] === 'cn' ? '简体中文' : 'English'})`}
                className="self-center sm:self-start shrink-0 rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-cyan-600"
              >
                <img
                  src={getPath(brochure, languages[0], 'webp')}
                  alt={t(`brochures.${brochure.id}.title`)}
                  width="160"
                  height="226"
                  loading="lazy"
                  className="w-40 aspect-[210/297] object-contain rounded-xl shadow-md"
                />
              </a>
              <div className="flex flex-col min-w-0 flex-1">
                <h3 className="text-xl font-bold text-slate-900 mb-3">{t(`brochures.${brochure.id}.title`)}</h3>
                <p className="text-sm leading-relaxed text-slate-500 font-medium mb-5">{t(`brochures.${brochure.id}.description`)}</p>
                <div className="mt-auto flex flex-col gap-3">
                  {languages.map((language, index) => (
                    <a
                      key={language}
                      href={getPath(brochure, language, 'pdf')}
                      download={`AquaMind-${brochure.file}-${language.toUpperCase()}.pdf`}
                      aria-label={`${t('brochures.download')} — ${t(`brochures.${brochure.id}.title`)} (${language === 'cn' ? '简体中文' : 'English'})`}
                      className={`px-4 py-3 text-sm flex items-center justify-between gap-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-cyan-600 ${index === 0 ? PLASTIC_BTN_PRIMARY : PLASTIC_BTN_SECONDARY}`}
                    >
                      <span className="flex items-center gap-2"><Download size={16} aria-hidden="true" />{language === 'cn' ? '简体中文' : 'English'}</span>
                      <span className="text-xs whitespace-nowrap">PDF · {brochure.sizes[language]}</span>
                    </a>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- HomePage Component ---

const HomePage = ({ setPage }) => {
    const { t, i18n } = useTranslation();
    const [activeSystemDemoIndex, setActiveSystemDemoIndex] = useState(0);
    const currentLanguage = i18n.resolvedLanguage || i18n.language;
    const usesChineseArtwork = (currentLanguage || 'en').startsWith('zh');
    const architectureImage = usesChineseArtwork
      ? '/images/september-update/platform-architecture.webp'
      : '/images/september-update/platform-architecture-en.png';
    const governanceImage = usesChineseArtwork
      ? '/images/september-update/brand-governance.webp'
      : '/images/september-update/brand-governance-en.png';
    const systemDemoImages = [
      '/images/september-update/system-knowledge.webp',
      '/images/september-update/system-robot.webp',
      '/images/september-update/system-character.webp',
      '/images/september-update/system-management.webp',
    ];

    useEffect(() => {
        setActiveSystemDemoIndex(0);
    }, [currentLanguage]);

    useEffect(() => {
        const interval = setInterval(() => {
            setActiveSystemDemoIndex((prev) => (prev + 1) % systemDemoImages.length);
        }, 4000);

        return () => clearInterval(interval);
    }, [systemDemoImages.length]);

    return (
        // Added relative z-10 to force it above background layers
        <div className="w-full relative z-10">
            <section className="relative pt-32 pb-16 px-6 container mx-auto grid lg:grid-cols-[0.85fr_1.35fr] gap-8 items-center min-h-[80vh]">
                <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7 }} className="relative z-10">
                    <h1 className="text-4xl md:text-6xl font-black leading-[1.15] mb-6 tracking-tight text-slate-900">{t('hero.title')}<br/><span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-500 to-violet-500">{t('hero.titleHighlight')}</span></h1>
                    <p className="text-lg md:text-xl font-medium text-slate-500 mb-8 max-w-lg leading-relaxed">{t('hero.subtitle')}</p>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <button onClick={() => document.getElementById('product-video')?.scrollIntoView({ behavior: 'smooth' })} className={`px-6 py-4 text-sm flex items-center justify-center gap-2 ${PLASTIC_BTN_PRIMARY}`}>{t('hero.cta')}</button>
                        <button onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })} className={`px-6 py-4 text-sm flex items-center justify-center gap-2 ${PLASTIC_BTN_SECONDARY}`}>{t('hero.secondaryCta')} <ArrowRight size={16} /></button>
                    </div>
                </motion.div>
                <motion.img initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} src="/images/september-update/hero-platform.webp" width="1100" height="763" alt={t('hero.imageAlt')} className="w-full [mask-image:linear-gradient(to_right,transparent,black_8%,black_95%,transparent)]" fetchpriority="high" />
            </section>

            <PromoVideoSection />

            <section id="platform-overview" className="py-20 px-6 bg-white/50 relative z-10">
                <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
                    <img src={architectureImage} width="660" height="610" alt={t('platformOverview.imageAlt')} className="w-full rounded-3xl" loading="lazy" />
                    <div>
                        <h2 className="text-3xl md:text-5xl font-black leading-tight text-slate-900 mb-6">{t('platformOverview.title')} <span className="text-cyan-500">{t('platformOverview.titleHighlight')}</span></h2>
                        <p className="text-lg text-slate-500 font-medium leading-relaxed mb-8">{t('platformOverview.subtitle')}</p>
                        <ul className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {t('platformOverview.connections', { returnObjects: true }).map((item) => <li key={item} className="border border-cyan-200 rounded-xl bg-white/70 p-3 text-center text-cyan-700 font-bold">{item}</li>)}
                        </ul>
                    </div>
                </div>
            </section>

            {/* --- SYSTEM SCREENSHOT --- */}
            <section id="system-demo" className="relative z-10 py-20 px-6 bg-gradient-to-b from-white/50 to-white">
                <div className="max-w-6xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-12"
                    >
                        <div className="inline-flex items-center gap-2 bg-cyan-50 text-cyan-600 px-4 py-1.5 rounded-full font-mono text-xs font-bold mb-6 border border-cyan-100">
                            <Sparkles size={14} />
                            {t('systemDemo.badge')}
                        </div>
                        <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">{t('systemDemo.title')}</h2>
                        <p className="text-slate-500 text-lg max-w-2xl mx-auto">{t('systemDemo.subtitle')}</p>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white bg-white"
                    >
                        <div className="absolute inset-0 bg-gradient-to-br from-cyan-400/10 to-violet-400/10"></div>
                        <div className="relative z-10 overflow-hidden">
                            <motion.div
                                className="flex"
                                animate={{ x: `-${activeSystemDemoIndex * 100}%` }}
                                transition={{ duration: 0.65, ease: 'easeInOut' }}
                            >
                                {systemDemoImages.map((image) => (
                                    <img
                                        key={`${currentLanguage}-${image}`}
                                        src={image}
                                        alt={`${t('systemDemo.title')} ${systemDemoImages.indexOf(image) + 1}`}
                                        className="w-full aspect-[1.5] object-contain shrink-0"
                                    />
                                ))}
                            </motion.div>
                        </div>
                        <div className="absolute bottom-5 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2 rounded-full border border-white/40 bg-white/25 px-3 py-2 backdrop-blur-md">
                            {systemDemoImages.map((image, index) => (
                                <button
                                    key={image}
                                    type="button"
                                    onClick={() => setActiveSystemDemoIndex(index)}
                                    aria-label={`${t('systemDemo.title')} ${index + 1}`}
                                    aria-pressed={activeSystemDemoIndex === index}
                                    className={`h-2.5 rounded-full transition-all duration-300 ${
                                        activeSystemDemoIndex === index
                                          ? 'w-8 bg-cyan-600'
                                          : 'w-2.5 bg-slate-300 hover:bg-cyan-400'
                                    }`}
                                />
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* --- RE-INSERTED WHY SECTION INTO HOMEPAGE --- */}
            <section id="why" className="relative z-10 py-32 px-6">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-6">{t('why.title')} <span className="text-cyan-500">{t('why.titleHighlight')}</span></h2>
                        <p className="text-slate-500 text-lg max-w-2xl mx-auto font-medium">{t('why.subtitle')}</p>
                    </div>
                    
                    <div className="grid md:grid-cols-3 gap-8">
                         {/* Card 1: Stand Out */}
                         <div className={`${GLASS_CARD} p-8 flex flex-col items-start h-full`}>
                            <div className="w-14 h-14 bg-pink-100 rounded-2xl flex items-center justify-center text-pink-600 mb-6 border border-pink-200 shadow-sm">
                                <Sparkles size={28} />
                            </div>
                            <h3 className="text-xl font-black text-slate-900 mb-3 uppercase tracking-wide">{t('why.standOut.title')}</h3>
                            <p className="text-slate-600 leading-relaxed font-medium">
                               {t('why.standOut.description')}
                            </p>
                         </div>

                         {/* Card 2: Speed */}
                         <div className={`${GLASS_CARD} p-8 flex flex-col items-start h-full`}>
                            <div className="w-14 h-14 bg-violet-100 rounded-2xl flex items-center justify-center text-violet-600 mb-6 border border-violet-200 shadow-sm">
                                <Zap size={28} />
                            </div>
                            <h3 className="text-xl font-black text-slate-900 mb-3 uppercase tracking-wide">{t('why.speed.title')}</h3>
                            <p className="text-slate-600 leading-relaxed font-medium">
                               {t('why.speed.description')}
                            </p>
                         </div>

                         {/* Card 3: Cost */}
                         <div className={`${GLASS_CARD} p-8 flex flex-col items-start h-full`}>
                            <div className="w-14 h-14 bg-cyan-100 rounded-2xl flex items-center justify-center text-cyan-600 mb-6 border border-cyan-200 shadow-sm">
                                <TrendingUp size={28} />
                            </div>
                            <h3 className="text-xl font-black text-slate-900 mb-3 uppercase tracking-wide">{t('why.cost.title')}</h3>
                            <p className="text-slate-600 leading-relaxed font-medium">
                               {t('why.cost.description')}
                            </p>
                         </div>
                    </div>
                    
                    <div className="mt-16 text-center">
                         <button onClick={() => setPage('why')} className={`px-8 py-3 text-sm uppercase tracking-widest ${PLASTIC_BTN_SECONDARY}`}>
                            {t('why.readFullStory')}
                         </button>
                    </div>
                </div>
            </section>

            <section id="features" className="py-32 px-6 container mx-auto relative z-10">
                <div className="max-w-4xl mx-auto text-center mb-24">
                    <h2 className="text-4xl md:text-5xl font-black mb-6 text-slate-900 tracking-tight">{t('features.title')} <br/><span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-500 to-blue-600">{t('features.titleHighlight')}</span></h2>
                    <p className="text-slate-500 font-medium text-lg max-w-2xl mx-auto">{t('features.subtitle')}</p>
                </div>
                <div className="grid lg:grid-cols-3 gap-8 md:gap-10">
                    <FeatureCard icon={Zap} title={t('features.hyperAutomation.title')} desc={t('features.hyperAutomation.description')} delay={0.1} />
                    <FeatureCard icon={Layers} title={t('features.holographic.title')} desc={t('features.holographic.description')} delay={0.2} />
                    <FeatureCard icon={Store} title={t('features.phygital.title')} desc={t('features.phygital.description')} delay={0.3} />
                </div>
            </section>

            <section id="governance" className="py-24 px-6 relative bg-white/50 border-y border-white/60">
                <div className="max-w-7xl mx-auto grid lg:grid-cols-[0.8fr_1.2fr] gap-12 items-center">
                    <div>
                        <div className="flex items-center gap-2 text-amber-500 text-xs font-bold mb-6"><ShieldCheck size={16} /> {t('governance.badge')}</div>
                        <h2 className="text-4xl md:text-5xl font-black mb-6 text-slate-900 leading-tight">{t('governance.title')}<br/><span className="text-slate-400">{t('governance.titleHighlight')}</span></h2>
                        <div className="rounded-2xl bg-white/80 border border-sky-100 p-6 space-y-4 shadow-sm">
                            <p className="text-right"><span className="inline-block bg-cyan-500 text-white px-4 py-3 rounded-xl">{t('governance.exampleQuestion')}</span></p>
                            <p className="text-slate-600 font-medium leading-relaxed">{t('governance.exampleResponse')}</p>
                        </div>
                    </div>
                    <img src={governanceImage} width="780" height="580" alt={t('governance.imageAlt')} className="w-full rounded-3xl" loading="lazy" />
                </div>
            </section>

            <section id="phygital" className="py-20 px-6 relative z-10 overflow-hidden">
                <div className="container mx-auto mb-10 text-center">
                    <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-6 tracking-tight">{t('phygital.title')} <span className="text-cyan-500">{t('phygital.titleHighlight')}</span></h2>
                    <p className="text-slate-500 font-medium text-xl">{t('phygital.subtitle')}</p>
                </div>
                <motion.div style={{ perspective: 1000 }} className="flex flex-col lg:flex-row gap-8 justify-center items-stretch max-w-4xl mx-auto">
                    <motion.div className={`${GLASS_CARD} p-6 w-full lg:w-1/3 flex flex-col items-center text-center group`}>
                        <div className="w-full aspect-square bg-slate-100 rounded-2xl mb-5 relative overflow-hidden shadow-inner group-hover:shadow-lg transition-all duration-500">
                            <img src="/images/september-update/smart-plush.webp" alt={t('phygital.digitalIntelligence.title')} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"/>
                        </div>
                        <h3 className="text-2xl font-bold text-slate-800 mb-3 uppercase tracking-wide">{t('phygital.digitalIntelligence.title')}</h3>
                        <p className="text-slate-500 font-medium text-sm">{t('phygital.digitalIntelligence.description')}</p>
                    </motion.div>
                    <motion.div className={`${GLASS_CARD} p-6 w-full lg:w-1/3 flex flex-col items-center text-center group`}>
                        <div className="w-full aspect-square bg-slate-100 rounded-2xl mb-5 relative overflow-hidden shadow-inner group-hover:shadow-lg transition-all duration-500">
                            <img src="/images/september-update/digital-installation.webp" alt={t('phygital.interactiveDisplays.title')} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"/>
                        </div>
                        <h3 className="text-2xl font-bold text-slate-800 mb-3 uppercase tracking-wide">{t('phygital.interactiveDisplays.title')}</h3>
                        <p className="text-slate-500 font-medium text-sm">{t('phygital.interactiveDisplays.description')}</p>
                    </motion.div>
                    <motion.div className={`${GLASS_CARD} p-6 w-full lg:w-1/3 flex flex-col items-center text-center group`}>
                        <div className="w-full aspect-square bg-slate-100 rounded-2xl mb-5 relative overflow-hidden shadow-inner group-hover:shadow-lg transition-all duration-500">
                            <img src="/images/september-update/physical-robot.webp" alt={t('phygital.physicalMascots.title')} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"/>
                        </div>
                        <h3 className="text-2xl font-bold text-slate-800 mb-3 uppercase tracking-wide">{t('phygital.physicalMascots.title')}</h3>
                        <p className="text-slate-500 font-medium text-sm">{t('phygital.physicalMascots.description')}</p>
                    </motion.div>
                </motion.div>
            </section>

            <section id="pricing" className="py-32 relative z-10">
                <div className="container mx-auto px-6">
                    <div className="text-center mb-20">
                        <h2 className="text-5xl font-black mb-6 text-slate-900 tracking-tight">{t('pricing.title')} <span className="text-slate-400">{t('pricing.titleHighlight')}</span></h2>
                        <p className="text-slate-500 font-medium text-lg max-w-2xl mx-auto">{t('pricing.subtitle')}</p>
                    </div>
                    <div className="grid lg:grid-cols-3 gap-8 items-stretch max-w-6xl mx-auto">
                        <PricingCard tier={t('pricing.starter.tier')} price={t('pricing.starter.price')} audience={t('pricing.starter.audience')} features={t('pricing.starter.features', { returnObjects: true })} />
                        <PricingCard tier={t('pricing.professional.tier')} price={t('pricing.professional.price')} audience={t('pricing.professional.audience')} recommended={true} features={t('pricing.professional.features', { returnObjects: true })} />
                        <PricingCard tier={t('pricing.enterprise.tier')} price={t('pricing.enterprise.price')} audience={t('pricing.enterprise.audience')} features={t('pricing.enterprise.features', { returnObjects: true })} />
                    </div>
                </div>
            </section>

            <BrochureSection />

            <section id="contact" className="py-32 px-6 relative z-10">
                <div className="max-w-4xl mx-auto bg-white/60 backdrop-blur-xl border border-white rounded-[3rem] p-8 md:p-16 shadow-[0_20px_50px_rgba(14,165,233,0.15)] relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-100/50 rounded-full blur-[80px] -z-10 animate-pulse"></div>
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-violet-100/50 rounded-full blur-[80px] -z-10"></div>
                    <div className="text-center mb-12">
                        <div className="inline-block bg-cyan-50 text-cyan-600 px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest mb-4 border border-cyan-100">{t('contact.badge')}</div>
                        <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-4">{t('contact.title')} <span className="text-cyan-500">{t('contact.titleHighlight')}</span> {t('contact.titleEnd')}</h2>
                        <p className="text-slate-500 font-medium text-lg">{t('contact.subtitle')}</p>
                    </div>
                    <ContactForm />
                </div>
            </section>
        </div>
    );
};

const LegalPage = ({ type }) => {
    const { toHomeSection } = useAppNavigation();
    const { localizedPath } = useLocaleRoute();
    const { t } = useTranslation();
    const pageKey = type === 'privacy' ? 'privacy' : 'terms';
    const sections = t(`legal.${pageKey}.sections`, { returnObjects: true });

    return (
        <div className="pt-32 pb-24 px-6 container mx-auto relative z-10">
            <motion.div
                className="max-w-4xl mx-auto mb-16 text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
            >
                <div className="inline-flex items-center gap-2 bg-white/60 border border-sky-100 px-4 py-1.5 rounded-full font-mono text-cyan-600 text-xs font-bold mb-6 shadow-sm">
                    <ShieldCheck size={14} />
                    {t(`legal.${pageKey}.badge`)}
                </div>
                <h1 className="text-4xl md:text-6xl font-black text-slate-900 mb-5 tracking-tight">
                    {t(`legal.${pageKey}.title`)}
                </h1>
                <p className="text-slate-500 text-lg md:text-xl font-medium leading-relaxed">
                    {t(`legal.${pageKey}.intro`)}
                </p>
                <p className="mt-4 text-xs uppercase tracking-[0.24em] text-slate-400 font-bold">
                    {t('legal.lastUpdated')}
                </p>
            </motion.div>

            <div className="max-w-5xl mx-auto space-y-6">
                {Array.isArray(sections) ? sections.map((section, index) => (
                    <motion.section
                        key={section.heading || index}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 0.5, delay: index * 0.04 }}
                        className={`${GLASS_CARD} p-8 md:p-10`}
                    >
                        <h2 className="text-2xl md:text-3xl font-black text-slate-900 mb-4 tracking-tight">
                            {section.heading}
                        </h2>
                        <p className="text-slate-600 leading-relaxed font-medium mb-5">
                            {section.body}
                        </p>
                        {Array.isArray(section.points) && section.points.length > 0 ? (
                            <ul className="space-y-3">
                                {section.points.map((point, pointIndex) => (
                                    <li key={pointIndex} className="flex gap-3 text-slate-600 leading-relaxed font-medium">
                                        <span className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-cyan-500" />
                                        <span>{point}</span>
                                    </li>
                                ))}
                            </ul>
                        ) : null}
                    </motion.section>
                )) : null}
            </div>

            <div className="max-w-5xl mx-auto mt-12 flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                    to={localizedPath('home')}
                    className={`inline-block px-8 py-3 text-sm uppercase tracking-widest ${PLASTIC_BTN_SECONDARY}`}
                >
                    {t('legal.backHome')}
                </Link>
                <button
                    type="button"
                    onClick={() => toHomeSection('contact')}
                    className={`px-8 py-3 text-sm uppercase tracking-widest ${PLASTIC_BTN_PRIMARY}`}
                >
                    {t('legal.contactAction')}
                </button>
            </div>
        </div>
    );
};

const HardwarePage = () => {
    const { toPage, toHomeSection } = useAppNavigation();
    const { t } = useTranslation();
    const capabilityCards = t('hardware.capabilities.cards', { returnObjects: true });
    const modules = t('hardware.modules.items', { returnObjects: true });
    const comparisonRows = t('hardware.comparison.rows', { returnObjects: true });
    const moduleIcons = [Sparkles, ShoppingBag, MapPin];
    const moduleDetailImages = [
        '/images/september-update/companion.webp',
        '/images/september-update/shopping-assistant.webp',
        '/images/september-update/roaming-assistant.webp',
    ];

    return (
        <div className="pt-32 pb-24 relative z-10">
            <section className="px-6">
                <div className="container mx-auto grid gap-12 lg:grid-cols-[0.85fr_1.35fr] items-center">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        className="max-w-3xl"
                    >
                        <div className="inline-flex items-center gap-2 bg-white/60 border border-sky-100 px-4 py-1.5 rounded-full font-mono text-cyan-600 text-xs font-bold mb-6 shadow-sm">
                            <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500"></span></span>
                            {t('hardware.badge')}
                        </div>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-slate-900 mb-6 tracking-tight leading-[1.15]">
                            {t('hardware.title')}<br/><span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-500 to-violet-500">{t('hardware.titleHighlight')}</span>
                        </h1>
                        <p className="text-lg md:text-xl text-slate-500 font-medium leading-relaxed max-w-2xl mb-8">
                            {t('hardware.subtitle')}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <button
                                onClick={() => toHomeSection('product-video')}
                                className={`px-8 py-4 text-sm uppercase tracking-widest ${PLASTIC_BTN_PRIMARY}`}
                            >
                                {t('hardware.primaryCta')}
                            </button>
                            <button
                                onClick={() => toHomeSection('contact')}
                                className={`px-8 py-4 text-sm uppercase tracking-widest ${PLASTIC_BTN_SECONDARY}`}
                            >
                                {t('hardware.secondaryCta')}
                            </button>
                        </div>
                    </motion.div>

                    <motion.img
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        src="/images/september-update/hero-hardware.webp" width="1155" height="848"
                        alt={t('hardware.imageAlt')}
                        className="w-full [mask-image:linear-gradient(to_right,transparent,black_8%,black_95%,transparent)]"
                    />
                </div>
            </section>

            <section className="px-6 py-20">
                <div className="container mx-auto grid gap-6 md:grid-cols-2 xl:grid-cols-4">
                    {Array.isArray(capabilityCards) ? capabilityCards.map((card, index) => (
                        <motion.div
                            key={card.title}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.5, delay: index * 0.08 }}
                            className={`${GLASS_CARD} p-8`}
                        >
                            <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl border border-white bg-gradient-to-br from-white to-sky-50 text-cyan-500 shadow-[0_4px_14px_rgba(14,165,233,0.12)]">
                                {index === 0 ? <Camera size={28} /> : index === 1 ? <Layers size={28} /> : index === 2 ? <Settings size={28} /> : <Network size={28} />}
                            </div>
                            <h2 className="mb-3 text-xl font-black text-slate-900">{card.title}</h2>
                            <p className="text-slate-500 font-medium leading-relaxed">{card.body}</p>
                        </motion.div>
                    )) : null}
                </div>
            </section>

            <section id="hardware-modules" className="px-6 py-8">
                <div className="container mx-auto">
                    <div className="max-w-3xl mb-12">
                        <div className="inline-flex items-center gap-2 bg-cyan-50 text-cyan-600 px-4 py-1.5 rounded-full font-mono text-xs font-bold mb-5 border border-cyan-100">
                            <Sparkles size={14} />
                            {t('hardware.modules.badge')}
                        </div>
                        <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-4 tracking-tight">
                            {t('hardware.modules.title')}
                        </h2>
                        <p className="text-slate-500 text-lg font-medium leading-relaxed">
                            {t('hardware.modules.subtitle')}
                        </p>
                    </div>

                    <div className="space-y-10">
                        {Array.isArray(modules) ? modules.map((module, index) => {
                            const Icon = moduleIcons[index] || Box;
                            const reverse = index % 2 === 1;

                            return (
                                <motion.section
                                    key={module.title}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.15 }}
                                    transition={{ duration: 0.55 }}
                                    className={`${GLASS_CARD} overflow-hidden`}
                                >
                                    <div className={`grid gap-0 lg:grid-cols-2 ${reverse ? 'lg:[&>.hardware-column-left]:order-2 lg:[&>.hardware-column-right]:order-1' : ''}`}>
                                        <div className="hardware-column-left flex flex-col border-b border-white/60 lg:border-b-0 lg:border-r lg:border-white/60">
                                            <div className="relative overflow-hidden bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.95),rgba(224,242,254,0.72)_45%,rgba(186,230,253,0.55)_100%)] p-4 md:p-6 lg:min-h-[420px] lg:flex lg:items-center">
                                                <img src={moduleDetailImages[index]} alt={module.title} className="relative z-10 w-full aspect-[1.5] rounded-[1.5rem] object-cover shadow-[0_18px_40px_rgba(15,23,42,0.12)]" />
                                                <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(15,23,42,0.08))]" />

                                            </div>
                                            <div className="border-t border-white/60 p-6 md:p-8">
                                                <div className="rounded-[1.75rem] border border-white/70 bg-white/55 p-6 shadow-[0_10px_25px_rgba(14,165,233,0.06)] h-full">
                                                    <p className="mb-4 text-xs font-bold uppercase tracking-[0.22em] text-cyan-500">{t('hardware.modules.whatFits')}</p>
                                                    <ul className="space-y-3">
                                                        {Array.isArray(module.useCases) ? module.useCases.map((item) => (
                                                            <li key={item} className="flex gap-3 text-slate-600 font-medium leading-relaxed">
                                                                <span className="mt-2 h-2 w-2 rounded-full bg-cyan-500 flex-shrink-0" />
                                                                <span>{item}</span>
                                                            </li>
                                                        )) : null}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="hardware-column-right flex flex-col">
                                            <div className="p-8 md:p-10 lg:p-12 lg:min-h-[420px]">
                                                <div className="mb-6 flex items-center gap-4">
                                                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-white bg-gradient-to-br from-white to-sky-50 text-cyan-500 shadow-[0_8px_24px_rgba(14,165,233,0.12)]">
                                                        <Icon size={30} />
                                                    </div>
                                                    <div>
                                                        <p className="text-xs font-bold uppercase tracking-[0.24em] text-cyan-500">{module.eyebrow}</p>
                                                        <h3 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight">{module.title}</h3>
                                                        <p className="mt-2 text-sm font-bold uppercase tracking-[0.22em] text-slate-400">{module.model}</p>
                                                    </div>
                                                </div>

                                                <p className="text-slate-500 text-lg font-medium leading-relaxed">
                                                    {module.description}
                                                </p>
                                            </div>

                                            <div className="border-t border-white/60 p-6 md:p-8">
                                                <div className="rounded-[1.75rem] border border-white/70 bg-white/55 p-6 shadow-[0_10px_25px_rgba(14,165,233,0.06)] h-full">
                                                    <p className="mb-4 text-xs font-bold uppercase tracking-[0.22em] text-cyan-500">{t('hardware.modules.systemLayer')}</p>
                                                    <ul className="space-y-3">
                                                        {Array.isArray(module.capabilities) ? module.capabilities.map((item) => (
                                                            <li key={item} className="flex gap-3 text-slate-600 font-medium leading-relaxed">
                                                                <span className="mt-2 h-2 w-2 rounded-full bg-violet-500 flex-shrink-0" />
                                                                <span>{item}</span>
                                                            </li>
                                                        )) : null}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </motion.section>
                            );
                        }) : null}
                    </div>
                </div>
            </section>

            <section className="px-6 py-20">
                <div className="container mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 0.55 }}
                        className={`${GLASS_CARD} p-6 md:p-8 lg:p-10`}
                    >
                        <div className="max-w-3xl mb-8">
                            <div className="inline-flex items-center gap-2 bg-cyan-50 text-cyan-600 px-4 py-1.5 rounded-full font-mono text-xs font-bold mb-5 border border-cyan-100">
                                <Layers size={14} />
                                {t('hardware.comparison.badge')}
                            </div>
                            <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-4 tracking-tight">{t('hardware.comparison.title')}</h2>
                            <p className="text-slate-500 text-lg font-medium leading-relaxed">{t('hardware.comparison.subtitle')}</p>
                        </div>

                        <div className="overflow-x-auto rounded-[1.75rem] border border-white/70 bg-white/60 shadow-[0_12px_30px_rgba(14,165,233,0.08)]">
                            <table className="min-w-full border-collapse text-left">
                                <thead>
                                    <tr className="border-b border-sky-100 bg-white/70">
                                        <th className="px-5 py-4 text-xs font-bold uppercase tracking-[0.22em] text-slate-400">{t('hardware.comparison.columns.model')}</th>
                                        <th className="px-5 py-4 text-xs font-bold uppercase tracking-[0.22em] text-slate-400">{t('hardware.comparison.columns.type')}</th>
                                        <th className="px-5 py-4 text-xs font-bold uppercase tracking-[0.22em] text-slate-400">{t('hardware.comparison.columns.positioning')}</th>
                                        <th className="px-5 py-4 text-xs font-bold uppercase tracking-[0.22em] text-slate-400">{t('hardware.comparison.columns.interaction')}</th>
                                        <th className="px-5 py-4 text-xs font-bold uppercase tracking-[0.22em] text-slate-400">{t('hardware.comparison.columns.mobility')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Array.isArray(comparisonRows) ? comparisonRows.map((row, index) => (
                                        <tr key={row.model} className={`${index !== comparisonRows.length - 1 ? 'border-b border-sky-100/80' : ''} hover:bg-cyan-50/40 transition-colors`}>
                                            <td className="px-5 py-5 align-top">
                                                <div className="font-black text-slate-900">{row.model}</div>
                                                <div className="mt-1 text-sm font-medium text-cyan-600">{row.label}</div>
                                            </td>
                                            <td className="px-5 py-5 text-sm font-medium leading-relaxed text-slate-600">{row.type}</td>
                                            <td className="px-5 py-5 text-sm font-bold text-slate-700">{row.positioning}</td>
                                            <td className="px-5 py-5 text-sm font-medium text-slate-600">{row.interaction}</td>
                                            <td className="px-5 py-5 text-sm font-medium text-slate-600">{row.mobility}</td>
                                        </tr>
                                    )) : null}
                                </tbody>
                            </table>
                        </div>
                    </motion.div>
                </div>
            </section>

            <BrochureSection retailOnly />

            <section className="px-6">
                <div className="container mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.55 }}
                        className="rounded-[2.5rem] border border-white/70 bg-white/60 p-8 md:p-12 shadow-[0_20px_50px_rgba(14,165,233,0.12)] backdrop-blur-xl relative overflow-hidden"
                    >
                        <div className="absolute top-0 right-0 h-56 w-56 rounded-full bg-cyan-100/70 blur-[100px]" />
                        <div className="absolute bottom-0 left-0 h-56 w-56 rounded-full bg-violet-100/70 blur-[100px]" />
                        <div className="relative z-10 max-w-3xl">
                            <p className="mb-4 text-xs font-bold uppercase tracking-[0.24em] text-cyan-500">{t('hardware.cta.badge')}</p>
                            <h2 className="mb-4 text-4xl md:text-5xl font-black text-slate-900 tracking-tight">{t('hardware.cta.title')}</h2>
                            <p className="mb-8 text-lg font-medium leading-relaxed text-slate-500">{t('hardware.cta.subtitle')}</p>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <button
                                    onClick={() => toHomeSection('contact')}
                                    className={`px-8 py-4 text-sm uppercase tracking-widest ${PLASTIC_BTN_PRIMARY}`}
                                >
                                    {t('hardware.cta.primary')}
                                </button>
                                <button
                                    onClick={() => toPage('home')}
                                    className={`px-8 py-4 text-sm uppercase tracking-widest ${PLASTIC_BTN_SECONDARY}`}
                                >
                                    {t('hardware.cta.secondary')}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default function App() {
  const { t } = useTranslation();
  const location = useLocation();
  const { localizedPath, page } = useLocaleRoute();
  const { toPage } = useAppNavigation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.scrollTo(0, 0);

    if (!location.hash) {
      return;
    }

    const sectionId = location.hash.replace('#', '');
    window.setTimeout(() => {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    }, 150);
  }, [location.pathname, location.hash]);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  if (page === 'home' || page === 'homepagePreview') {
    return (
      <>
        <LocaleSync />
        <SeoManager page={page} />
        <HomeLanding Brochures={BrochureSection} ContactForm={ContactForm} />
      </>
    );
  }

  return (
    <div className={`min-h-screen bg-[${COLORS.bg}] text-slate-900 font-sans selection:bg-cyan-200 selection:text-cyan-900 overflow-x-hidden`}>
      <LocaleSync />
      <SeoManager page={page} />
      <ScrollWaterDrop />

      {/* --- BACKGROUND EFFECTS --- */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden bg-[#F0F9FF]">
         <motion.div className="absolute top-0 left-0 h-full w-[200%] opacity-40" animate={{ x: ["0%", "-50%"] }} transition={{ duration: 25, repeat: Infinity, ease: "linear" }}>
            <svg viewBox="0 0 2880 1000" preserveAspectRatio="none" className="w-full h-full text-cyan-200 fill-current"><path d="M0,400 C480,500 960,300 1440,400 C1920,500 2400,300 2880,400 V1000 H0 Z" /></svg>
         </motion.div>
         <motion.div className="absolute top-[20%] left-0 h-full w-[200%] opacity-30" animate={{ x: ["-50%", "0%"] }} transition={{ duration: 35, repeat: Infinity, ease: "linear" }}>
            <svg viewBox="0 0 2880 1000" preserveAspectRatio="none" className="w-full h-full text-cyan-300 fill-current"><path d="M0,350 C400,250 1040,450 1440,350 C1840,250 2480,450 2880,350 V1000 H0 Z" /></svg>
         </motion.div>
         <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] bg-cyan-200/30 rounded-full blur-[100px] animate-pulse"></div>
         <div className="absolute bottom-[-10%] left-[-5%] w-[600px] h-[600px] bg-indigo-200/30 rounded-full blur-[100px]"></div>
         <div className="absolute inset-0 opacity-[0.06] bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.65),transparent_18%),radial-gradient(circle_at_80%_30%,rgba(255,255,255,0.4),transparent_22%),radial-gradient(circle_at_30%_80%,rgba(255,255,255,0.35),transparent_20%)] mix-blend-overlay"></div>
      </div>

      {/* --- NAV --- */}
      <nav className="fixed top-0 w-full z-50 px-6 py-2 transition-all duration-300">
        <div className="container mx-auto">
            <div className={`bg-white/70 backdrop-blur-xl rounded-2xl px-4 py-2 border border-white/60 flex justify-between items-center max-w-6xl mx-auto shadow-lg shadow-sky-100/50`}>
                <Link to={localizedPath('home')} className="flex items-center gap-4">
                    <img src="/Aquamind_blue_logo.png" alt="AquaMind Logo" className="w-32 sm:w-48 h-16 object-contain" />
                </Link>
                <div className="hidden xl:flex gap-1 items-center">
                    <Link to={localizedPath('home', 'features')} className="px-5 py-2 rounded-lg text-sm font-bold text-slate-500 hover:text-cyan-600 hover:bg-sky-50 transition-all">{t('nav.features')}</Link>
                    <Link to={localizedPath('home', 'pricing')} className="px-5 py-2 rounded-lg text-sm font-bold text-slate-500 hover:text-cyan-600 hover:bg-sky-50 transition-all">{t('nav.pricing')}</Link>
                    <Link to={localizedPath('hardware')} className="px-5 py-2 rounded-lg text-sm font-bold text-slate-500 hover:text-cyan-600 hover:bg-sky-50 transition-all">{t('nav.hardware')}</Link>
                    <Link to={localizedPath('why')} className="px-5 py-2 rounded-lg text-sm font-bold text-slate-500 hover:text-cyan-600 hover:bg-sky-50 transition-all">{t('why.title')} {t('why.titleHighlight')}</Link>
                    <Link to={localizedPath('home', 'contact')} className={`px-6 py-2.5 text-xs uppercase tracking-wider ${PLASTIC_BTN_SECONDARY}`}>{t('nav.contact')}</Link>
                    <button type="button" className={`ml-2 px-6 py-2.5 text-xs uppercase tracking-wider ${PLASTIC_BTN_PRIMARY}`}>{t('nav.deploy')}</button>
                    <div className="ml-2"><LanguageSwitcher /></div>
                </div>
                <div className="flex xl:hidden items-center gap-2">
                    <LanguageSwitcher />
                    <button type="button" aria-label={t('nav.menu')} aria-expanded={mobileMenuOpen} className="text-slate-500" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}><Menu size={24} /></button>
                </div>
            </div>
        </div>
        <AnimatePresence>{mobileMenuOpen && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="absolute top-20 left-6 right-6 bg-white border border-sky-100 rounded-2xl shadow-xl p-6 xl:hidden z-50 backdrop-blur-xl">
            <div className="flex flex-col gap-4 font-bold text-lg text-center text-slate-800">
              <Link to={localizedPath('home')} onClick={closeMobileMenu}>{t('nav.home')}</Link>
              <Link to={localizedPath('hardware')} onClick={closeMobileMenu}>{t('nav.hardware')}</Link>
              <Link to={localizedPath('why')} onClick={closeMobileMenu}>{t('why.title')} {t('why.titleHighlight')}</Link>
              <Link to={localizedPath('privacy')} onClick={closeMobileMenu}>{t('footer.privacy')}</Link>
              <Link to={localizedPath('terms')} onClick={closeMobileMenu}>{t('footer.terms')}</Link>
            </div>
          </motion.div>
        )}</AnimatePresence>
      </nav>

      {page === 'homepagePrevious' && <HomePage setPage={toPage} />}
      {page === 'why' && <WhyPage />}
      {page === 'hardware' && <HardwarePage />}
      {page === 'privacy' && <LegalPage type="privacy" />}
      {page === 'terms' && <LegalPage type="terms" />}

      {/* --- FOOTER --- */}
      <footer className="bg-slate-900 text-white py-20 px-6 border-t border-slate-800 relative overflow-hidden">
        <div className="absolute bottom-0 left-0 text-[12rem] font-black text-white/5 leading-none select-none pointer-events-none tracking-tighter">AQUA</div>
        <div className="container mx-auto flex flex-col md:flex-row justify-between items-start gap-12 relative z-10">
          <div className="text-left max-w-md">
            <div className="flex items-center gap-4 mb-8"><img src="/Aquamind_white_logo.png" alt="AquaMind Logo" className="w-56 h-20 object-contain" /></div>
            <p className="text-slate-400 mb-10 leading-relaxed font-light">{t('footer.description')}<br/>{t('footer.descriptionExtended')}</p>
            <div className="flex gap-2 p-1 bg-white/5 rounded-xl border border-white/10 max-w-sm"><input type="email" placeholder={t('footer.newsletter')} className="min-w-0 flex-1 bg-transparent px-4 py-2 text-white placeholder:text-slate-500 focus:outline-none text-sm" /><button type="button" className={`shrink-0 whitespace-nowrap px-6 py-2 text-xs uppercase font-bold rounded-lg ${PLASTIC_BTN_PRIMARY}`}>{t('footer.join')}</button></div>
          </div>
            <div className="grid grid-cols-2 gap-16 font-bold text-xs uppercase tracking-widest">
            <div className="flex flex-col gap-4">
              <span className="text-cyan-500 mb-2">{t('footer.platform')}</span>
              <Link to={localizedPath('home', 'brochures')} className="text-left text-slate-400 hover:text-white transition-colors">{t('brochures.nav')}</Link>
              <Link to={localizedPath('home', 'features')} className="text-left text-slate-400 hover:text-white transition-colors">{t('nav.features')}</Link>
              <Link to={localizedPath('hardware')} className="text-left text-slate-400 hover:text-white transition-colors">{t('nav.hardware')}</Link>
              <Link to={localizedPath('home', 'pricing')} className="text-left text-slate-400 hover:text-white transition-colors">{t('nav.pricing')}</Link>
              <span className="text-left text-slate-400">{t('nav.login')}</span>
            </div>
            <div className="flex flex-col gap-4">
              <span className="text-cyan-500 mb-2">{t('footer.legal')}</span>
              <Link to={localizedPath('privacy')} className="text-left text-slate-400 hover:text-white transition-colors">{t('footer.privacy')}</Link>
              <Link to={localizedPath('terms')} className="text-left text-slate-400 hover:text-white transition-colors">{t('footer.terms')}</Link>
              <Link to={localizedPath('home', 'contact')} className="text-left text-slate-400 hover:text-white transition-colors">{t('footer.contactLink')}</Link>
            </div>
          </div>
        </div>
        <div className="container mx-auto mt-20 pt-8 border-t border-slate-800 text-xs text-slate-500 flex justify-between items-center font-mono"><span>{t('footer.copyright')}</span><div className="flex gap-4"><div className="hover:text-cyan-400 cursor-pointer transition-colors">TWITTER</div><div className="hover:text-cyan-400 cursor-pointer transition-colors">LINKEDIN</div><div className="hover:text-cyan-400 cursor-pointer transition-colors">GITHUB</div></div></div>
      </footer>
    </div>
  );
}
